import { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import { productMatchesAudienceFilter, audienceBadgeLabel } from '../constants/productAudience';
import './ProductCatalog.css';

const AUDIENCE_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'junior', label: 'Junior' },
  { id: 'senior', label: 'Senior' }
];

const TYPE_OPTIONS = ['Tape Ball', 'Hard Ball'];

const TYPE_CONFIG = {
  'Tape Ball': {
    tagline: 'Training & casual play',
    blurb: 'Tape-friendly bats, balls, gloves, and footwear for practice and street cricket.'
  },
  'Hard Ball': {
    tagline: 'Match & league standard',
    blurb: 'Full protective range for leather-ball cricket — from bats to helmets and keeping gear.'
  }
};

function TypeIconTape() {
  return (
    <svg className="type-option-icon-svg" viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="24" cy="24" r="18" stroke="currentColor" strokeWidth="2.5" opacity="0.35" />
      <circle cx="24" cy="24" r="12" stroke="currentColor" strokeWidth="2" strokeDasharray="4 3" />
      <path
        d="M24 8v6M24 34v6M8 24h6M34 24h6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        opacity="0.5"
      />
    </svg>
  );
}

function TypeIconHard() {
  return (
    <svg className="type-option-icon-svg" viewBox="0 0 48 48" fill="none" aria-hidden>
      <circle cx="24" cy="24" r="16" stroke="currentColor" strokeWidth="2.5" />
      <path
        d="M14 20c4 8 16 8 20 0M14 28c4-8 16-8 20 0"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
      <ellipse cx="24" cy="24" rx="3" ry="16" stroke="currentColor" strokeWidth="1.25" opacity="0.45" />
    </svg>
  );
}

const TYPE_ICONS = {
  'Tape Ball': TypeIconTape,
  'Hard Ball': TypeIconHard
};

const CATEGORIES_BY_TYPE = {
  'Tape Ball': ['Ball', 'Bat', 'Gloves', 'Shoes'],
  'Hard Ball': ['Bat', 'Ball', 'Shoes', 'Bag', 'Accessories', 'Gloves', 'Pads', 'Helmet', 'Keeping Gloves', 'Keeping Pads']
};

function ProductDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  // rough check: if text is short enough it won't overflow 3 lines
  const isLong = text.length > 120;
  return (
    <div className="product-card-desc-wrap">
      <p className={`product-card-desc${!expanded && isLong ? ' product-card-desc--clamped' : ''}`}>
        {text}
      </p>
      {isLong && (
        <button
          type="button"
          className="product-card-desc-toggle"
          onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
        >
          {expanded ? 'See less' : 'See more'}
        </button>
      )}
    </div>
  );
}

function formatPriceDisplay(price) {
  if (price == null || price === '') return '—';
  const n = typeof price === 'number' ? price : String(price).replace(/[^0-9.]/g, '');
  if (!n) return '—';
  return `£${Number(n).toFixed(2)}`;
}

function ProductImageSlider({ product }) {
  const allImages = [product.image, ...(Array.isArray(product.images) ? product.images : [])].filter(Boolean);
  const [idx, setIdx] = useState(0);
  const touchStartX = useRef(null);

  if (allImages.length <= 1) {
    return product.image
      ? <img src={product.image} alt={product.name} />
      : <div className="product-card-placeholder">No image</div>;
  }

  const prev = (e) => { e.stopPropagation(); setIdx(i => (i - 1 + allImages.length) % allImages.length); };
  const next = (e) => { e.stopPropagation(); setIdx(i => (i + 1) % allImages.length); };

  return (
    <div
      className="product-slider"
      onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => {
        if (touchStartX.current === null) return;
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) diff > 0 ? setIdx(i => (i+1) % allImages.length) : setIdx(i => (i-1+allImages.length) % allImages.length);
        touchStartX.current = null;
      }}
    >
      <img src={allImages[idx]} alt={`${product.name} ${idx + 1}`} />
      <button className="slider-arrow slider-arrow--prev" onClick={prev} aria-label="Previous">‹</button>
      <button className="slider-arrow slider-arrow--next" onClick={next} aria-label="Next">›</button>
      <div className="slider-dots">
        {allImages.map((_, i) => (
          <button key={i} className={`slider-dot${i === idx ? ' active' : ''}`} onClick={(e) => { e.stopPropagation(); setIdx(i); }} aria-label={`Image ${i+1}`} />
        ))}
      </div>
    </div>
  );
}

const ProductCatalog = ({ onProductSelect }) => {
  const [selectedType, setSelectedType] = useState('Tape Ball');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [audienceFilter, setAudienceFilter] = useState('all');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    setAudienceFilter('all');
  }, [selectedCategory]);

  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('status', 'Active')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const getProductsByCategory = (category) =>
    products.filter((p) => p.type === selectedType && p.category === category);

  const visibleCategories = (CATEGORIES_BY_TYPE[selectedType] || [])
    .filter((category) => getProductsByCategory(category).length > 0)
    .map((category) => ({ id: category, name: category }));

  if (loading) {
    return (
      <div className="product-catalog">
        <div className="catalog-loading" role="status" aria-live="polite">
          <div className="catalog-loading-spinner" />
          <p className="catalog-loading-text">Loading catalogue…</p>
        </div>
      </div>
    );
  }

  if (!selectedCategory) {
    return (
      <div className="product-catalog">
        <header className="catalog-hero">
          <p className="catalog-eyebrow">Product catalogue</p>
          <h1 className="catalog-title">Shop by type</h1>
          <p className="catalog-subtitle">
            Pick your format — we&apos;ll show categories and gear that match how you play.
          </p>
        </header>

        <section className="catalog-type-section" aria-labelledby="catalog-type-heading">
          <div className="catalog-type-section-head">
            <h2 id="catalog-type-heading" className="catalog-type-heading">
              Choose your format
            </h2>
            <p className="catalog-type-lead">Select tape ball or hard ball to filter the store.</p>
          </div>
          <div className="catalog-type-grid" role="group" aria-label="Kit type">
            {TYPE_OPTIONS.map((type) => {
              const Icon = TYPE_ICONS[type];
              const cfg = TYPE_CONFIG[type];
              const isActive = selectedType === type;
              return (
                <button
                  key={type}
                  type="button"
                  className={`type-option-card${isActive ? ' type-option-card--active' : ''}`}
                  onClick={() => {
                    setSelectedType(type);
                    setSelectedCategory(null);
                    setAudienceFilter('all');
                  }}
                  aria-pressed={isActive}
                >
                  <span className="type-option-card-glow" aria-hidden />
                  <span className="type-option-icon" aria-hidden>
                    <Icon />
                  </span>
                  <span className="type-option-body">
                    <span className="type-option-title">{type}</span>
                    <span className="type-option-tagline">{cfg.tagline}</span>
                    <span className="type-option-blurb">{cfg.blurb}</span>
                  </span>
                  <span className={`type-option-check${isActive ? ' type-option-check--on' : ''}`} aria-hidden>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        {visibleCategories.length === 0 ? (
          <div className="catalog-empty catalog-empty-rich">
            <div className="catalog-empty-visual" aria-hidden>
              <svg viewBox="0 0 120 120" className="catalog-empty-svg">
                <circle cx="60" cy="60" r="44" className="catalog-empty-blob" />
                <path
                  d="M40 72h40M50 52h20M46 62h28"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  opacity="0.35"
                />
                <circle cx="60" cy="48" r="12" stroke="currentColor" strokeWidth="2.5" fill="none" opacity="0.5" />
              </svg>
            </div>
            <h3 className="catalog-empty-title">No products in this format yet</h3>
            <p className="catalog-empty-text">
              We&apos;re stocking {selectedType.toLowerCase()} gear — check back soon, or ask your admin to add
              inventory for this type.
            </p>
            <p className="catalog-empty-hint">
              Tip: switch format above to see the other range, if available.
            </p>
          </div>
        ) : (
          <>
            <div className="catalog-categories-bar">
              <h2 className="catalog-categories-title">Browse by category</h2>
              <p className="catalog-categories-caption">
                Showing categories for <strong>{selectedType}</strong>
              </p>
            </div>
            <div className="categories-grid">
            {visibleCategories.map((category) => {
              const count = getProductsByCategory(category.id).length;
              return (
                <article
                  key={category.id}
                  className="category-card"
                  data-category={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  style={{ cursor: 'pointer' }}
                >
                  <h2 className="category-name">{category.name}</h2>
                  <p className="category-meta">
                    {count} {count === 1 ? 'product' : 'products'}
                  </p>
                  <span className="category-action">
                    View range
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </article>
              );
            })}
          </div>
          </>
        )}
      </div>
    );
  }

  const categoryProducts = getProductsByCategory(selectedCategory).filter((p) =>
    productMatchesAudienceFilter(p, audienceFilter)
  );

  return (
    <div className="product-catalog">
      <header className="catalog-toolbar">
        <button type="button" className="catalog-back" onClick={() => setSelectedCategory(null)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          All categories
        </button>
        <div className="catalog-toolbar-text">
          <h1 className="catalog-toolbar-title">{selectedCategory}</h1>
          <p className="catalog-toolbar-count">
            {categoryProducts.length} {categoryProducts.length === 1 ? 'item' : 'items'} · {selectedType}
          </p>
        </div>
      </header>

      <div className="catalog-audience-bar" role="group" aria-label="Filter by junior or senior">
        <span className="catalog-audience-label">Show</span>
        <div className="catalog-audience-pills">
          {AUDIENCE_FILTERS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={`catalog-audience-pill${audienceFilter === f.id ? ' catalog-audience-pill--active' : ''}`}
              onClick={() => setAudienceFilter(f.id)}
              aria-pressed={audienceFilter === f.id}
            >
              {f.label}
            </button>
          ))}
        </div>
        <p className="catalog-audience-hint">
          {audienceFilter === 'junior' && 'Junior-only products.'}
          {audienceFilter === 'senior' && 'Senior-only products.'}
          {audienceFilter === 'all' && 'All products; tags show who each item is for.'}
        </p>
      </div>

      {categoryProducts.length === 0 ? (
        <div className="catalog-empty catalog-empty-inline">
          <p>No products in this category.</p>
          <button type="button" className="catalog-empty-link" onClick={() => setSelectedCategory(null)}>
            Return to categories
          </button>
        </div>
      ) : (
        <div className="products-grid">
          {categoryProducts.map((product) => (
            <article key={product.id} className="product-card">
              <div className="product-card-media">
                <ProductImageSlider product={product} />
                <span className="product-card-badge">{product.type || selectedType}</span>
                <span className="product-card-audience" title="Allocated for">
                  {audienceBadgeLabel(product.level)}
                </span>
              </div>
              <div className="product-card-body">
                <h2 className="product-card-title">{product.name}</h2>
                <ProductDescription text={product.description} />
                <div className="product-card-footer">
                  <span className="product-card-price">{formatPriceDisplay(product.price)}</span>
                  <button
                    type="button"
                    className="product-card-cta"
                    onClick={() =>
                      onProductSelect({
                        ...product,
                        price: formatPriceDisplay(product.price)
                      })
                    }
                  >
                    Buy now
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductCatalog;
